/** @jsxImportSource @emotion/react */
import { Button } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'
import Input from 'components/Input'
import Popper from 'components/Popper'
import * as classes from './styles'

export const CELL_TYPES = {
  EMPTY_SPACE: 'empty-space',
  CHECK: 'check',
  CROSS: 'cross',
  TEXT: 'text',
  CHECK_AND_TEXT: 'check-and-text',
  CROSS_AND_TEXT: 'cross-and-text',
} as const

export type CellTypeKey = keyof typeof CELL_TYPES
export type CellType = (typeof CELL_TYPES)[CellTypeKey]

const TYPE_OPTIONS: Array<{ type: CellType; title: string }> = [
  {
    type: CELL_TYPES.TEXT,
    title: 'Only Text',
  },
  {
    type: CELL_TYPES.CHECK_AND_TEXT,
    title: 'Check and Text',
  },
  {
    type: CELL_TYPES.CROSS_AND_TEXT,
    title: 'Cross and Text',
  },
  {
    type: CELL_TYPES.CHECK,
    title: 'Check Mark',
  },
  {
    type: CELL_TYPES.CROSS,
    title: 'Cross Mark',
  },
  {
    type: CELL_TYPES.EMPTY_SPACE,
    title: 'Empty Space',
  },
]

export type Value = {
  type: CellType
  text?: string
  columnId: string
}

export type CellProps = {
  value: Value
  onChange: (value: Value) => void
  isEditable?: boolean
}

export const Cell = ({ value, onChange, isEditable = false }: CellProps) => {
  const renderValue = () => {
    const textField = isEditable ? (
      <Input
        value={value.text ?? ''}
        onChange={(text) => onChange({ ...value, text })}
        size="small"
        css={classes.input}
        required
        data-testid="cell-input"
      />
    ) : (
      <>{value.text ?? ''}</>
    )

    switch (value.type) {
      case CELL_TYPES.EMPTY_SPACE:
        return ''
      case CELL_TYPES.CHECK:
        return <CheckIcon />
      case CELL_TYPES.CROSS:
        return <CrossIcon />
      case CELL_TYPES.TEXT:
        return textField
      case CELL_TYPES.CHECK_AND_TEXT:
        return (
          <>
            <CheckIcon />
            {textField}
          </>
        )
      case CELL_TYPES.CROSS_AND_TEXT:
        return (
          <>
            <CrossIcon />
            {textField}
          </>
        )
    }
  }

  const renderPopperContent = (onClose: () => void) => (
    <ul css={classes.popperList}>
      {TYPE_OPTIONS.map(({ type, title }) => (
        <li key={type}>
          <Button
            css={classes.popperListButton}
            variant="outlined"
            onClick={() => {
              onChange({ ...value, type })
              onClose()
            }}
          >
            {title}
          </Button>
        </li>
      ))}
    </ul>
  )

  return (
    <div css={classes.cell}>
      <div css={classes.value} data-testid="render-value">
        {renderValue()}
      </div>
      {isEditable && (
        <Popper trigger="onClick" getContent={renderPopperContent}>
          <Button variant="outlined" color="blue" size="small" css={classes.iconButton} data-testid="cell-type-button">
            <AddRoundedIcon />
          </Button>
        </Popper>
      )}
    </div>
  )
}
